import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { Button } from "@seneca/senekit";

import {
  passwordsResetSuccessReset,
  resetPasswordsFailure,
  resetPasswordsSuccess
} from "seneca-common/features/teachers/state";
import { getNewPassword } from "seneca-common/features/teachers/state/selectors";
import { pluralizeWord } from "seneca-common/utils/functions/formatting/pluralize";
import useSelectorOnCondition from "seneca-common/utils/hooks/selectors/useSelectorOnCondition";

import { useResetPasswords } from "features/teachers/pages/school/hooks/useResetPasswords";

import { ToastType } from "../consts";
import { showToast } from "../toast";
import ToastLayout from "../ToastLayout";

type Props = {
  userIds: string[];
  success: boolean;
  schoolId: string;
  onClose: () => void;
};

function ResetPasswordsToast({
  userIds,
  success,
  schoolId,
  closeToast
}: Props & { closeToast: () => void }) {
  const newPassword = useSelectorOnCondition(!success, getNewPassword);
  const dispatch = useDispatch();

  const numItems = userIds.length;
  const passwordReference = pluralizeWord(numItems, "password");
  const title = success
    ? `Reset ${passwordReference} successful!`
    : `Error resetting ${passwordReference}`;
  const description = success
    ? `You have successfully reset ${userIds.length} ${passwordReference}`
    : "Something went wrong. Please try again or get in touch with learnmore@seneca.io";

  const resetLoadingText =
    "Retrying, don't worry, we'll notify you as soon as it's done!";

  const onSuccess = useCallback(() => {
    dispatch(resetPasswordsSuccess());
    closeToast();
  }, [dispatch, closeToast]);

  const onError = useCallback(() => {
    dispatch(resetPasswordsFailure(newPassword!));
    closeToast();
  }, [dispatch, newPassword, closeToast]);

  const {
    mutate: resetPasswords,
    isLoading,
    isSuccess
  } = useResetPasswords({
    onSuccess,
    onError
  });

  const handleRetry = useCallback(async () => {
    if (newPassword) {
      await resetPasswords({ schoolId, userIds, newPassword });
      dispatch(passwordsResetSuccessReset());
    }
  }, [dispatch, resetPasswords, schoolId, userIds, newPassword]);

  return (
    <ToastLayout type={success ? ToastType.success : ToastType.error}>
      {{
        title,
        description: isLoading || isSuccess ? resetLoadingText : description,
        button: !success && (
          <Button tone="error" onClick={handleRetry} loading={isLoading}>
            Retry
          </Button>
        )
      }}
    </ToastLayout>
  );
}

export function showResetPasswordsToast(props: Props) {
  return showToast(
    ResetPasswordsToast,
    {
      toastId: "resetPasswordsToast",
      onClose: props.onClose,
      autoClose: props.success || props.success === null ? undefined : false
    },
    props
  );
}
